var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var HierarchyValidatorV2Config_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { getRenamedColumns } from './toolConfigUtil';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
const VALID_COLNAME = 'Valid';
const HIERARCHY_ERROR_COLNAME = 'Hierarchy Error';
const schema = {
    $id: 'urn:wisepipe-common-pipe:HierarchyValidatorV2Config',
    type: 'object',
    properties: {
        keyColumn: { type: 'string' },
        keyNameColumn: { type: 'string' },
        parentKeyColumn: { type: 'string' },
        parentKeyNameColumn: { type: 'string' }
    },
    required: ['keyColumn', 'parentKeyColumn']
};
let HierarchyValidatorV2Config = HierarchyValidatorV2Config_1 = class HierarchyValidatorV2Config extends ToolConfig {
    constructor(keyColumn, parentKeyColumn, keyNameColumn, parentKeyNameColumn, customFormats) {
        super(ToolType.HIERARCHY_VALIDATORV2, customFormats);
        this.keyColumn = keyColumn;
        this.parentKeyColumn = parentKeyColumn;
        this.keyNameColumn = keyNameColumn;
        this.parentKeyNameColumn = parentKeyNameColumn;
        JSON.validate(this, HierarchyValidatorV2Config_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return HierarchyValidatorV2Config_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return HierarchyValidatorV2Config_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return HierarchyValidatorV2Config_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return HierarchyValidatorV2Config_1.outs;
    }
    static get in() {
        return HierarchyValidatorV2Config_1.ins[0];
    }
    static get out() {
        return HierarchyValidatorV2Config_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.HIERARCHY_VALIDATOR.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CLEAN], this.ins, this.outs, ['hierarchy', 'validator'], true);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        const output = new Map();
        source.forEach((s) => {
            switch (s.name) {
                case this.keyColumn:
                    output.set('keyColumn', s);
                    break;
                case this.parentKeyColumn:
                    output.set('parentKeyColumn', s);
                    break;
                case this.keyNameColumn:
                    output.set('keyNameColumn', s);
                    break;
                case this.parentKeyNameColumn:
                    output.set('parentKeyNameColumn', s);
                    break;
                default:
                    break;
            }
        });
        const exp = tsb.export(this.out);
        exp.copy(this.in, output.get('keyColumn'), false, true);
        exp.copy(this.in, output.get('keyNameColumn'), false, true);
        exp.copy(this.in, output.get('parentKeyColumn'), false, true);
        exp.copy(this.in, output.get('parentKeyNameColumn'), false, true);
        const dependents = [output.get('keyColumn'), output.get('parentKeyColumn')];
        const usedColumns = [this.keyColumn, this.keyNameColumn, this.parentKeyColumn, this.parentKeyNameColumn].filter((c) => c); // Take all defined columns
        const validColumnName = getRenamedColumns(usedColumns, VALID_COLNAME, ToolType.HIERARCHY_VALIDATORV2);
        const hierarchyErrorColumnName = getRenamedColumns(usedColumns, HIERARCHY_ERROR_COLNAME, ToolType.HIERARCHY_VALIDATORV2);
        exp.makeNew(new Column(validColumnName, 'boolean'), dependents, true);
        exp.makeNew(new Column(hierarchyErrorColumnName, 'string'), dependents, true);
        exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const usedColumns = [this.keyColumn, this.keyNameColumn, this.parentKeyColumn, this.parentKeyNameColumn].filter((c) => c); // Take all defined columns
        const inputs = new Set(usedColumns);
        if (usedColumns.length !== inputs.size) {
            // Duplicate columns are selected
            const duplicateColumns = usedColumns.filter((c, i) => usedColumns.indexOf(c) !== i);
            return ToolValidationResult.getInvalidResult(`Duplicate columns selected: ${duplicateColumns.join(', ')}.`, `keyColumn, keyNameColumn, parentKeyColumn, parentKeyNameColumn need to be unique.`);
        }
        // const outputs = [
        // 	getRenamedColumns(usedColumns, VALID_COLNAME, ToolType.HIERARCHY_VALIDATORV2),
        // 	getRenamedColumns(usedColumns, HIERARCHY_ERROR_COLNAME, ToolType.HIERARCHY_VALIDATORV2)
        // ];
        const sourceValidationResult = validateSourceWithDetail(source, inputs, []);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
HierarchyValidatorV2Config = HierarchyValidatorV2Config_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.HIERARCHY_VALIDATORV2)
], HierarchyValidatorV2Config);
export { HierarchyValidatorV2Config };
//# sourceMappingURL=hierarchyValidatorV2Config.js.map